import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { User } from "firebase/auth";
import useRouter from "../../app/router/UserRoutes";
import { baseUrl } from "../../config/serverConfig";
import { UserMenu } from "../../types/authTypes";
import { AxiosResponseType, DefaultResponseType } from "../../types/axiosTypes";
import useAxios from "../axios/useAxios";
import { signOutUser } from "../firebase/signOut";

// Retry delays (ms) for transient failures (backend restart / network blip).
//   3 s → 8 s → 15 s → 25 s → 40 s  (total window ~91 s before giving up)
const RETRY_DELAYS = [3000, 8000, 15000, 25000, 40000];

// After all retries are exhausted, poll silently every 30 s so the app
// auto-recovers when the backend comes back without any user interaction.
const AUTO_RECOVERY_INTERVAL = 30000;

export type AuthState = "loading" | "ready" | "error";

interface LoginData {
  clientMenus: UserMenu[];
  role: string;
}

export const useUserRoutes = (
  user: User | null | undefined,
  setUserMenu: React.Dispatch<React.SetStateAction<UserMenu[]>>,
  setCurrentUser: React.Dispatch<React.SetStateAction<string>>
) => {
  const navigate = useNavigate();

  const [userData, setUserData] = useState<UserMenu[] | undefined>();
  const [authState, setAuthState] = useState<AuthState>("loading");
  // Incrementing retryTrigger re-runs the main auth effect.
  const [retryTrigger, setRetryTrigger] = useState(0);

  const failCount = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoRecoveryRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { fetchData }: AxiosResponseType = useAxios({ method: "get" });

  const clearRetryTimer = () => {
    if (retryTimerRef.current !== null) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  };

  const clearAutoRecovery = () => {
    if (autoRecoveryRef.current !== null) {
      clearInterval(autoRecoveryRef.current);
      autoRecoveryRef.current = null;
    }
  };

  // Manual retry: resets all state and starts over from scratch.
  const retryAuth = useCallback(() => {
    clearRetryTimer();
    clearAutoRecovery();
    failCount.current = 0;
    setAuthState("loading");
    setRetryTrigger((t) => t + 1);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    clearRetryTimer();
    clearAutoRecovery();
    failCount.current = 0;

    if (!user) {
      setAuthState("loading");
      return;
    }

    setAuthState("loading");

    let cancelled = false;

    const attemptLogin = async (): Promise<boolean> => {
      try {
        const response: DefaultResponseType<LoginData> = await fetchData(
          `${baseUrl}/user/login-user`
        );

        if (cancelled) return false;

        // ── Definitieve afwijzing ──────────────────────────────────────────
        // 401 = ongeldig token, 403 = account/kantoor gedeactiveerd of rol
        // zonder toegang → uitloggen. Netwerk-/timeoutfouten hebben geen
        // statusCode en mogen NOOIT tot uitloggen leiden.
        if (
          response.error &&
          (response.statusCode === 401 || response.statusCode === 403)
        ) {
          console.log(`[useUserRoutes] ${response.statusCode} — signing out`);
          signOutUser();
          return false;
        }

        // ── Succes ─────────────────────────────────────────────────────────
        // Ook een geldige maar lege menulijst telt als definitief antwoord:
        // het tweede effect logt dan netjes uit i.p.v. eindeloos te retryen.
        const clientMenus = response?.data?.clientMenus;
        if (!response.error && Array.isArray(clientMenus)) {
          failCount.current = 0;
          setUserData(clientMenus);
          setCurrentUser(response.data.role ?? "");
          clearAutoRecovery();
          return true;
        }

        return false; // transient failure
      } catch {
        // fetchData hoort zelf nooit te throwen; mocht het tóch gebeuren dan
        // mag dat de retry-keten niet doden — behandel als transient.
        return false;
      }
    };

    const startRetrySequence = async () => {
      if (cancelled) return;

      const ok = await attemptLogin();
      if (ok || cancelled) return;

      // ── Retry sequence ────────────────────────────────────────────────────
      const tryNext = () => {
        if (cancelled) return;
        const delay = RETRY_DELAYS[failCount.current];
        failCount.current += 1;

        if (delay !== undefined) {
          console.log(
            `[useUserRoutes] attempt ${failCount.current} failed — retrying in ${delay}ms`
          );
          retryTimerRef.current = setTimeout(async () => {
            if (cancelled) return;
            const ok = await attemptLogin();
            if (!ok && !cancelled) tryNext();
          }, delay);
        } else {
          // All retries exhausted — switch to error state + silent background polling.
          console.log("[useUserRoutes] all retries exhausted — entering error state");
          setAuthState("error");

          autoRecoveryRef.current = setInterval(async () => {
            if (cancelled) {
              clearAutoRecovery();
              return;
            }
            console.log("[useUserRoutes] auto-recovery: probing backend...");
            const ok = await attemptLogin();
            if (ok) clearAutoRecovery();
          }, AUTO_RECOVERY_INTERVAL);
        }
      };

      tryNext();
    };

    startRetrySequence();

    return () => {
      cancelled = true;
      clearRetryTimer();
      clearAutoRecovery();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, retryTrigger]);

  useEffect(() => {
    if (!userData) return;
    if (!userData.length) {
      signOutUser();
      navigate("/", { replace: true });
      return;
    }
    setAuthState("ready");
    setUserMenu(userData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const routes = useRouter({ userData });

  return { routes, authState, retryAuth };
};
